import { format, parse } from 'date-fns'
import { useEffect, useMemo } from 'react'
import {
  useCurrentDocument,
  useDocumentExtractedDenialData,
  type DataExtractionFieldName,
} from '~/entities/document'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { useAppForm } from '~/shared/lib/form'
import {
  Button,
  DateInput,
  IconButton,
  ScrollArea,
  Select,
  Text,
  Textarea,
  Textfield,
} from '~/shared/ui'
import type { PatientMatch } from '../../../model'

const PA_VENDOR_OPTIONS = [
  'NATERA_RCM',
  'GLIDIAN',
  'INNOBOT',
  'MGML',
  'CMT',
  'NATERA_MA',
  'NATERA_BILLING_MRR_PA',
  'INFINX - TRUE PA DAILY EMR',
  'INFINX - TRUE PA SCHEDULED',
  'NATERA_MRR_PA',
  'NATERA_RCM_AETNA_PREDETERMINATION',
  'INFINX',
  'BLANK',
] as const

interface NewMatchProps {
  newlyAddedMatch: PatientMatch | null
  onSubmit: (match: PatientMatch) => void
  onClose: () => void
  isPaMatchingCutover?: boolean
  resolvedTestName?: string | null
}

export function NewMatch({
  newlyAddedMatch,
  onSubmit,
  onClose,
  isPaMatchingCutover = false,
  resolvedTestName,
}: NewMatchProps) {
  const {
    data: { document },
  } = useCurrentDocument()
  const { data } = useDocumentExtractedDenialData({ documentKey: document.key })
  const isPeerToPeerEnabled = useIsFeatureEnabled('2026-05-peer-to-peer')
  const isBilledAmountEnabled = useIsFeatureEnabled('2026-05-billed-amount-patient-matching')
  const isCarrierClaimIdEnabled = useIsFeatureEnabled('2026-06-carrier-claim-id')

  const fieldsMap = useMemo(() => {
    return (data?.fields ?? []).reduce<Record<DataExtractionFieldName, string | undefined>>(
      (acc, field) => {
        acc[field.name as DataExtractionFieldName] = field.value as string
        return acc
      },
      {} as Record<DataExtractionFieldName, string | undefined>,
    )
  }, [data])

  const defaultValues: Omit<PatientMatch, 'matchIndex' | 'newlyAdded'> = useMemo(
    () =>
      newlyAddedMatch
        ? {
            patient_first_name: newlyAddedMatch.patient_first_name,
            patient_last_name: newlyAddedMatch.patient_last_name,
            date_of_birth: newlyAddedMatch.date_of_birth,
            lims_date_of_service: newlyAddedMatch.lims_date_of_service,
            allproccodes_claim: newlyAddedMatch.allproccodes_claim,
            billprice: newlyAddedMatch.billprice,
            claim_id: newlyAddedMatch.claim_id,
            visitfid: newlyAddedMatch.visitfid,
            lims_casefile_id: newlyAddedMatch.lims_casefile_id,
            policy_number: newlyAddedMatch.policy_number,
            notes: newlyAddedMatch.notes ?? '',
            payorname: newlyAddedMatch.payorname ?? '',
            test_name: newlyAddedMatch.test_name ?? '',
            pa_vendor: newlyAddedMatch.pa_vendor ?? '',
            reference_number: newlyAddedMatch.reference_number ?? '',
            authorization_code: newlyAddedMatch.authorization_code ?? '',
          }
        : isPaMatchingCutover
          ? {
              patient_first_name: fieldsMap.patient_first_name,
              patient_last_name: fieldsMap.patient_last_name,
              date_of_birth: fieldsMap.patient_dob,
              payorname: fieldsMap.insurance_company ?? '',
              test_name: resolvedTestName ?? fieldsMap.test_name ?? '',
              lims_date_of_service: fieldsMap.service_date_from,
              allproccodes_claim: fieldsMap.cpt_code,
              policy_number: '',
              lims_casefile_id: '',
              reference_number: '',
              authorization_code: '',
              pa_vendor: '',
              notes: '',
            }
          : {
              patient_first_name: fieldsMap.patient_first_name,
              patient_last_name: fieldsMap.patient_last_name,
              date_of_birth: fieldsMap.patient_dob,
              lims_date_of_service: fieldsMap.service_date_from,
              allproccodes_claim: fieldsMap.cpt_code,
              billprice: fieldsMap.total_amount ?? '',
              claim_id: '',
              visitfid: '',
              lims_casefile_id: '',
              policy_number: '',
              notes: '',
            },
    [fieldsMap, newlyAddedMatch, isPaMatchingCutover, resolvedTestName],
  )

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      onSubmit({
        ...value,
        newlyAdded: true,
        matchIndex: -1,
      })
    },
  })

  useEffect(() => {
    form.validate('change')
  }, [form])

  return (
    <form
      className="flex h-full flex-col"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <div className="flex shrink-0 items-center justify-between px-6 py-4">
        <Text appearance="H6">{newlyAddedMatch ? 'Newly Added Match' : 'Add New Match'}</Text>
        <IconButton
          materialIcon="close"
          size="medium"
          container="small"
          onClick={onClose}
        />
      </div>

      <ScrollArea className="flex h-full flex-col">
        <div className="px-6 pt-3">
          <div className="flex flex-col gap-3 pb-2">
            <Text appearance="OVERLINE">Payor document data</Text>
            <form.Field
              name="patient_first_name"
              children={(formField) => (
                <Textfield
                  label="Patient First Name"
                  name={formField.name}
                  value={formField.state.value ?? ''}
                  onChange={(e) => formField.handleChange(e.target.value)}
                  onBlur={formField.handleBlur}
                />
              )}
            />
            <form.Field
              name="patient_last_name"
              children={(formField) => (
                <Textfield
                  label="Patient Last Name"
                  name={formField.name}
                  value={formField.state.value ?? ''}
                  onChange={(e) => formField.handleChange(e.target.value)}
                  onBlur={formField.handleBlur}
                />
              )}
            />
            <form.Field
              name="date_of_birth"
              children={(formField) => (
                <DateInput
                  label="Birth Date"
                  name={formField.name}
                  date={
                    formField.state.value
                      ? parse(formField.state.value, 'yyyy-MM-dd', new Date())
                      : undefined
                  }
                  onDateChange={(date) =>
                    formField.handleChange(date ? format(date, 'yyyy-MM-dd') : '')
                  }
                  onBlur={formField.handleBlur}
                />
              )}
            />
            {isPaMatchingCutover && (
              <form.Field
                name="payorname"
                children={(formField) => (
                  <Textfield
                    label="Payor Name Extracted"
                    name={formField.name}
                    value={formField.state.value ?? ''}
                    onChange={(e) => formField.handleChange(e.target.value)}
                    onBlur={formField.handleBlur}
                  />
                )}
              />
            )}
            {isPaMatchingCutover && (
              <form.Field
                name="test_name"
                children={(formField) => (
                  <Textfield
                    label="Test Name"
                    name={formField.name}
                    value={formField.state.value ?? ''}
                    onChange={(e) => formField.handleChange(e.target.value)}
                    onBlur={formField.handleBlur}
                  />
                )}
              />
            )}
            <form.Field
              name="lims_date_of_service"
              children={(formField) => (
                <DateInput
                  label="Date of Service"
                  name={formField.name}
                  date={
                    formField.state.value
                      ? parse(formField.state.value, 'yyyy-MM-dd', new Date())
                      : undefined
                  }
                  onDateChange={(date) =>
                    formField.handleChange(date ? format(date, 'yyyy-MM-dd') : '')
                  }
                  onBlur={formField.handleBlur}
                />
              )}
            />
            {isPaMatchingCutover ? (
              <form.Field
                name="allproccodes_claim"
                children={(formField) => (
                  <Textfield
                    label="CPT Code(s)"
                    name={formField.name}
                    value={formField.state.value ?? ''}
                    onChange={(e) => formField.handleChange(e.target.value)}
                    onBlur={formField.handleBlur}
                  />
                )}
              />
            ) : (
              <>
                {isPeerToPeerEnabled && (
                  <form.Field
                    name="allproccodes_claim"
                    children={(formField) => (
                      <Textfield
                        label="CPT Code"
                        name={formField.name}
                        value={formField.state.value ?? ''}
                        onChange={(e) => formField.handleChange(e.target.value)}
                        onBlur={formField.handleBlur}
                      />
                    )}
                  />
                )}
                {isBilledAmountEnabled && (
                  <form.Field
                    name="billprice"
                    children={(formField) => (
                      <Textfield
                        label="Billed Amount"
                        name={formField.name}
                        value={formField.state.value ?? ''}
                        onChange={(e) => formField.handleChange(e.target.value)}
                        onBlur={formField.handleBlur}
                      />
                    )}
                  />
                )}
              </>
            )}

            <div className="bg-divider mt-3 mb-2 h-px" />

            <Text appearance="OVERLINE">Natera patient data</Text>

            {isPaMatchingCutover ? (
              <>
                <form.Field
                  name="policy_number"
                  children={(formField) => (
                    <Textfield
                      label="Member ID"
                      name={formField.name}
                      value={formField.state.value ?? ''}
                      onChange={(e) => formField.handleChange(e.target.value)}
                      onBlur={formField.handleBlur}
                    />
                  )}
                />
                <form.Field
                  name="lims_casefile_id"
                  validators={{
                    onChange: ({ value }) => (!value?.trim() ? 'Required' : undefined),
                  }}
                  children={(formField) => (
                    <Textfield
                      label="LIMS Case ID"
                      required
                      name={formField.name}
                      value={formField.state.value ?? ''}
                      onChange={(e) => formField.handleChange(e.target.value)}
                      onBlur={formField.handleBlur}
                    />
                  )}
                />
                <form.Field
                  name="reference_number"
                  children={(formField) => (
                    <Textfield
                      label="Reference Number"
                      name={formField.name}
                      value={formField.state.value ?? ''}
                      onChange={(e) => formField.handleChange(e.target.value)}
                      onBlur={formField.handleBlur}
                    />
                  )}
                />
                <form.Field
                  name="authorization_code"
                  children={(formField) => (
                    <Textfield
                      label="Authorization Code"
                      name={formField.name}
                      value={formField.state.value ?? ''}
                      onChange={(e) => formField.handleChange(e.target.value)}
                      onBlur={formField.handleBlur}
                    />
                  )}
                />
                <form.Field
                  name="pa_vendor"
                  children={(formField) => (
                    <Select
                      label="Prior Auth Vendor"
                      name={formField.name}
                      value={formField.state.value ?? ''}
                      onValueChange={(v) => formField.handleChange(String(v))}
                      onBlur={formField.handleBlur}
                    >
                      {PA_VENDOR_OPTIONS.map((vendor) => (
                        <Select.Item
                          key={vendor}
                          value={vendor}
                        >
                          {vendor}
                        </Select.Item>
                      ))}
                    </Select>
                  )}
                />
              </>
            ) : (
              <>
                <form.Field
                  name="claim_id"
                  children={(formField) => (
                    <Textfield
                      label={isCarrierClaimIdEnabled ? 'Natera Claim ID' : 'Claim ID'}
                      name={formField.name}
                      value={formField.state.value ?? ''}
                      onChange={(e) => formField.handleChange(e.target.value)}
                      onBlur={formField.handleBlur}
                    />
                  )}
                />
                {isCarrierClaimIdEnabled && (
                  <form.Field
                    name="carrier_claim_id"
                    children={(formField) => (
                      <Textfield
                        label="Carrier Claim ID"
                        name={formField.name}
                        value={formField.state.value ?? ''}
                        onChange={(e) => formField.handleChange(e.target.value)}
                        onBlur={formField.handleBlur}
                      />
                    )}
                  />
                )}
                <form.Field
                  name="visitfid"
                  children={(formField) => (
                    <Textfield
                      label="Visit ID"
                      name={formField.name}
                      value={formField.state.value ?? ''}
                      onChange={(e) => formField.handleChange(e.target.value)}
                      onBlur={formField.handleBlur}
                    />
                  )}
                />
                <form.Field
                  name="lims_casefile_id"
                  children={(formField) => (
                    <Textfield
                      label="Case Number"
                      name={formField.name}
                      value={formField.state.value ?? ''}
                      onChange={(e) => formField.handleChange(e.target.value)}
                      onBlur={formField.handleBlur}
                    />
                  )}
                />
                <form.Field
                  name="policy_number"
                  children={(formField) => (
                    <Textfield
                      label="Member ID"
                      name={formField.name}
                      value={formField.state.value ?? ''}
                      onChange={(e) => formField.handleChange(e.target.value)}
                      onBlur={formField.handleBlur}
                    />
                  )}
                />
              </>
            )}
          </div>

          <div className="bg-divider my-3 h-px" />

          <form.Field
            name="notes"
            children={(formField) => (
              <div className="flex flex-col gap-3 pt-1">
                <Text appearance="OVERLINE">Leave your comment</Text>
                <Textarea
                  className="min-h-[85px]"
                  placeholder="Your comment"
                  name={formField.name}
                  value={formField.state.value}
                  onChange={(e) => formField.handleChange(e.target.value)}
                  onBlur={formField.handleBlur}
                />
              </div>
            )}
          />
        </div>
      </ScrollArea>

      <form.Subscribe
        selector={(state) => ({
          isSubmitting: state.isSubmitting,
          isValid: state.isValid,
        })}
        children={({ isSubmitting, isValid }) => {
          return (
            <div className="flex shrink-0 justify-end gap-4 p-4">
              <Button onClick={onClose}>Cancel</Button>
              {newlyAddedMatch ? (
                <Button
                  raised
                  onClick={form.handleSubmit}
                  disabled={isSubmitting || !isValid}
                >
                  Save
                </Button>
              ) : (
                <Button
                  raised
                  onClick={form.handleSubmit}
                  disabled={isSubmitting || !isValid}
                >
                  Add
                </Button>
              )}
            </div>
          )
        }}
      />
    </form>
  )
}
